import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { connectState } from '@examdojo/angular/util';
import { TimeAgoModule } from '@examdojo/core/date-time';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { ImageEditingType, QuestionEditingOptions } from '@examdojo/models/question-editor';
import {
  ChecklistConfig,
  extractImageInfo,
  extractMarkschemeInfo,
  extractMCQInfo,
  extractTableInfo,
  QuestionValidationHeaderComponent,
} from '@examdojo/question';
import { ReferenceQuestionUIModel } from '@examdojo/reference-question';
import {
  ConsensusFeedbackFormComponent,
  createDynamicSurveyConfig,
  Evaluation,
  PreviousEvaluationsComponent,
  QuestionReviewFeedbackFormState,
  QuestionType,
  REFERENCE_QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING,
  ReferenceQuestionReviewType,
  ReferenceQuestionReviewTypeBadgeComponent,
  ReferenceQuestionStatus,
  REVIEW_ACTION_TYPE_TO_REFERENCE_QUESTION_REVIEW_TYPE,
  ReviewItemHttpService,
} from '@examdojo/review';
import { UserQuery } from '@examdojo/user';
import baseConfig from '@settings/ibdp-reference-question-checklist.json';
import { combineLatest, EMPTY, startWith } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { shouldShowReferenceQuestionConsensusWarning } from '../../shared/consensus-utils';
import { ReferenceQuestionEditorItemsFormService } from '../ref-question-editor-items-form-service';
import { getReferenceQuestionReviewConfig } from '../ref-question-validation.model';

@Component({
  selector: 'dojo-reference-question-sidebar',
  standalone: true,
  imports: [
    QuestionValidationHeaderComponent,
    ConsensusFeedbackFormComponent,
    MatTabsModule,
    TimeAgoModule,
    MatExpansionModule,
    ReferenceQuestionReviewTypeBadgeComponent,
    NgTemplateOutlet,
    PreviousEvaluationsComponent,
  ],
  templateUrl: './reference-question-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'h-full w-full',
  },
  styles: [
    `
      :host {
        display: block;
      }

      ::ng-deep .mat-mdc-tab-labels {
        height: 45px;
      }

      ::ng-deep .mat-accordion {
        // Remove default material spacing
        .mat-expansion-panel:not(:first-child) {
          margin-top: 0 !important;
        }

        // Remove space-y conflicts
        & > * + * {
          margin-top: 0 !important;
        }
      }

      ::ng-deep .custom-expansion-panel {
        border: 1px solid theme('colors.neutral.200');
        border-radius: 12px; // Rounder corners
        box-shadow: none !important; // No shadows
        margin-bottom: 8px;

        &:not(.mat-expanded) {
          border-bottom: 1px solid theme('colors.neutral.200');
        }

        .mat-expansion-panel-header {
          height: auto;
          padding: 12px 16px;

          &:hover {
            background-color: theme('colors.neutral.50');
          }
        }

        .mat-expansion-panel-body {
          padding: 0 16px 8px 8px;
        }

        .mat-panel-title {
          margin: 0;
          width: 100%;
        }

        // When expanded, header bottom corners become square
        &.mat-expanded .mat-expansion-panel-header {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      }
    `,
  ],
})
export class ReferenceQuestionSidebarComponent {
  constructor(
    private readonly reviewItemHttpService: ReviewItemHttpService,
    private readonly referenceQuestionEditorItemsFormService: ReferenceQuestionEditorItemsFormService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly userQuery: UserQuery,
  ) {}

  readonly ReferenceQuestionReviewType = ReferenceQuestionReviewType;
  readonly checklistConfig = baseConfig as ChecklistConfig;

  readonly formStateChange = output<QuestionReviewFeedbackFormState>();

  readonly mode = input.required<ReferenceQuestionReviewType>();
  readonly refQuestion = input.required<ReferenceQuestionUIModel>();

  readonly calculatorAllowed = input.required<boolean>();
  readonly isSubmitting = input(false);

  readonly imageInfo = computed(() => extractImageInfo(this.refQuestion().question_items));
  readonly markschemeInfo = computed(() => extractMarkschemeInfo(this.refQuestion().question_items));
  readonly mcqInfo = computed(() => extractMCQInfo(this.refQuestion().question_items));
  readonly tableInfo = computed(() => extractTableInfo(this.refQuestion().question_items));

  readonly editingOptions = toSignal<QuestionEditingOptions | null, QuestionEditingOptions | null>(
    this.referenceQuestionEditorItemsFormService.editingOptions$,
    {
      initialValue: {
        allowImageEditing: [ImageEditingType.Original, ImageEditingType.Generated],
        allowQuestionEditing: true,
        allowMarkschemeEditing: true,
        allowMcqEditing: false,
        allowMarkschemeStructureEditing: false,
        allowMcqStructureEditing: false,
        hideBadges: false,
      },
    },
  );

  readonly allowImageEditing = computed(() => (this.editingOptions()?.allowImageEditing?.length ?? 0) > 0);
  readonly allowQuestionEditing = computed(() => this.editingOptions()?.allowQuestionEditing ?? false);
  readonly allowMarkschemeEditing = computed(() => this.editingOptions()?.allowMarkschemeEditing ?? false);

  readonly editingAllowed = computed(
    () => this.allowImageEditing() || this.allowQuestionEditing() || this.allowMarkschemeEditing(),
  );

  readonly dynamicSurveyConfig = computed(() => {
    return createDynamicSurveyConfig(
      this.checklistConfig as ChecklistConfig<ReferenceQuestionStatus>,
      this.markschemeInfo(),
      this.imageInfo(),
      this.mcqInfo(),
      this.tableInfo(),
      REFERENCE_QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING,
      this.mode(),
    );
  });

  readonly refQuestionId$ = toObservable(this.refQuestion).pipe(
    map((refQuestion) => refQuestion.id),
    distinctUntilChanged(),
  );

  readonly previousEvaluations$ = this.refQuestionId$.pipe(
    switchMap((refQuestionId) => this.reviewItemHttpService.getReferenceQuestionFeedback(refQuestionId)),
    this.errorHandlerService.catchHttpErrors(() => EMPTY),
  );

  readonly config = computed(() => getReferenceQuestionReviewConfig(this.mode()));
  readonly showPreviousFeedback = computed(() => this.config().showPreviousFeedback);

  readonly state = connectState({
    showConsensusWarning: combineLatest([
      this.previousEvaluations$,
      this.userQuery.active$,
      toObservable(this.mode),
    ]).pipe(
      map(([evaluations, user, mode]) =>
        user?.id ? shouldShowReferenceQuestionConsensusWarning(evaluations, user.id, mode) : false,
      ),
    ),
    previousEvaluations: combineLatest([toObservable(this.showPreviousFeedback), this.previousEvaluations$]).pipe(
      map(([showPreviousFeedback, evaluations]) =>
        evaluations
          .filter((e) => {
            const reviewType = REVIEW_ACTION_TYPE_TO_REFERENCE_QUESTION_REVIEW_TYPE[e.action_type];

            if (!reviewType) {
              console.error('Invalid review action type', e.action_type);
              return false;
            }

            return showPreviousFeedback.includes(reviewType);
          })
          .map((e) => {
            const reviewType = REVIEW_ACTION_TYPE_TO_REFERENCE_QUESTION_REVIEW_TYPE[e.action_type]!;

            const dynamicSurveyConfig: ChecklistConfig<ReferenceQuestionStatus> = createDynamicSurveyConfig(
              this.checklistConfig as ChecklistConfig<ReferenceQuestionStatus>,
              this.markschemeInfo(),
              this.imageInfo(),
              this.mcqInfo(),
              this.tableInfo(),
              REFERENCE_QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING,
              reviewType,
            );

            return {
              ...e,
              id: e.action_id,
              reviewType,
              config: dynamicSurveyConfig,
              questionType: QuestionType.Reference as const,
            } satisfies Evaluation;
          }),
      ),
      startWith([]),
    ),
  });
}
